import { Back, Circ, Elastic, Expo, TimelineLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import { Application } from "../Application";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import LightsOutGameModel from "../data/LightsOutGameModel";
import { LightsOutObjectType } from "../data/LightsOutObjectType";
import * as VO from "../data/VO";
import { AnimatedLightsOutObject } from "../gameobjects/AnimatedLightsOutObject";
import { Button } from "../gameobjects/Button";
import { Elmo } from "../gameobjects/Elmo";
import { LightsOutObject } from "../gameobjects/LightsOutObject";
import { Radio } from "../gameobjects/Radio";
import { StaticLightsOutObject } from "../gameobjects/StaticLightsOutObject";
import {LightsOutElmoBedroomLayout} from "../layout/LightsOutElmoBedroomLayout";
import {AbstractGameState} from "./AbstractGameState";
import { AbstractLightsOutGameState } from "./AbstractLightsOutGameState";

export class LightsOutElmoBedroomScreen extends AbstractLightsOutGameState {

    public static readonly NAME = "LightsOutElmoBedroomScreen";

    public readonly bg = this.addChild(new Sprite());
    public readonly rocket = this.addChild(new AnimatedLightsOutObject({
        type: LightsOutObjectType.Electricity,
        frames: 10,
        spriteSheet: ["lightsout-bedroom-1"],
    }));
    public readonly lightswitch = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly toybear = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.EasterEgg}));
    public readonly car = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.EasterEgg}));
    public readonly laptop = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Electricity}));
    public readonly helicopter = this.addChild(new AnimatedLightsOutObject({
        type: LightsOutObjectType.Electricity,
        frames: 3,
        spriteSheet: ["lightsout-bedroom-1"],
    }));
    public readonly lamp = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly ceilinglamp = this.addChild(new StaticLightsOutObject({type: LightsOutObjectType.Light}));
    public readonly dinosaur = this.addChild(new AnimatedLightsOutObject({
        type: LightsOutObjectType.EasterEgg,
        frames: 8,
        spriteSheet: ["lightsout-bedroom-1"],
        loopCount: 3,
    }));
    public readonly radio = this.addChild(new Radio());

    public readonly lampGlow = this.addChild(new Sprite());
    public readonly ceilinglampGlow = this.addChild(new Sprite());

    protected analyticsScreenName: string = "5a Lights Out_Elmo Room Screen";

    protected readonly roomID = "Bedroom";
    protected readonly roomNameVO = VO.LO_ROOM02;

    protected itemsToTurnOff = [
        this.rocket,
        this.radio,
        this.lightswitch,
        this.laptop,
        this.helicopter,
        this.lamp,
    ];

    protected _subscriptions: any[] = [];

    public init(pSize: PIXI.Point, pData?: any) {
        LightsOutElmoBedroomLayout.call(this);
        super.init(pSize, pData);

        this.helicopter.hoverSFX = "lights-out-helicopter";
        this.lamp.lightsOutSFX = "lights-out-lamp";
        this.lightswitch.lightsOutSFX = "lights-out-light-switch";
        this.lamp.lightsOutSFX = "lights-out-light-switch";
        this.rocket.hoverSFX = "lights-out-swinging-rocket";
        this.laptop.lightsOutSFX = "lights-out-electronics-button";
        this.radio.lightsOutSFX = "lights-out-electronics-button";
        this.car.clickSfx = "lights-out-toy-car";
        this.dinosaur.clickSfx = "lights-out-toy-dinosaur";
        this.toybear.clickSfx = "lights-out-teddy-squeak";
        this.toybear.clickSfxCanOverlap = false;

        this.makeObjectClickable(this.rocket);
        this.makeObjectClickable(this.radio);
        this.makeObjectClickable(this.laptop);
        this.makeObjectClickable(this.helicopter);
        this.makeObjectClickable(this.lamp);
        this.makeObjectClickable(this.dinosaur);
        this.makeObjectClickable(this.car);
        this.makeObjectClickable(this.toybear);
        this.makeSwitch(this.lightswitch, this.ceilinglamp);

        this._subscriptions.push([
            PubSub.subscribe("item turned on", (pTopic: "item turned on", pItem: LightsOutObject) => {
                if (pItem === this.car) {
                    TweenMax.to(this.car, 1, {pixi: {x: this.car.x - 20}}).yoyo(true).repeat(-1).repeatDelay(0.5);
                    this.car.interactive = false;
                }
                else if (pItem === this.toybear) {
                    new TimelineLite()
                        .to(this.toybear, 0.25, {pixi: {scaleX: 0.9, scaleY: 1.75}, ease: Back.easeOut})
                        .to(this.toybear, 0.1, {pixi: {scaleX: 1.5, scaleY: 0.5}})
                        .to(this.toybear, 0.5, {pixi: {scaleX: 1, scaleY: 1}, ease: Elastic.easeOut})
                    ;
                }
            }),
        ]);

        // randomize objects
        HLF.ArrayUtils.shuffle(this.itemsToTurnOff);
        // move already played objects to end of array
        this.itemsToTurnOff.sort((a, b) => LightsOutGameModel.rooms.Bedroom.itemsLeftOn.indexOf(a.name));
        // offset the array by one so it's not just flip flopping between two sets of three
        this.itemsToTurnOff.push(this.itemsToTurnOff.shift()!);

        LightsOutGameModel.rooms.Bedroom.itemsLeftOn = [];

        // disable all but three of them
        for (let i = 0; i < this.itemsToTurnOff.length; i++) {
            if (i < 3) {
                this.itemsToTurnOff[i].TurnOn();
                this.itemsToTurnOff[i].Highlight();
                LightsOutGameModel.rooms.Bedroom.itemsLeftOn.push(this.itemsToTurnOff[i].name);
            }
            else {
                this.itemsToTurnOff[i].TurnOn({animate: false}); // hacky fix for lights glow
                this.itemsToTurnOff[i].TurnOff({animate: false});
        }
        }

        HLF.PixiUtils.sendToFront(this.homeButton);
        HLF.PixiUtils.sendToFront(this.elmo);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set((pSize.x - 1664) / 2, 0);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
    }

    public destroy() {
        super.destroy();
        for (const subscription of this._subscriptions) {
            PubSub.unsubscribe(subscription);
        }
    }
}
